import React, { useEffect, useMemo, useState } from "react";
import { COL, ensureSpreadsheet, readAllRows } from "../api/sheets";
import {
  driveFileUrl,
  ensureAssetIcon,
  ensureCommonPicture,
  ensureMapsFolder,
  fetchLocalAssetBlob,
  findFileInFolder,
  renameAndMoveFile,
  resolveOrCreateFolder,
  uploadCommonPicture,
  uploadFileToFolder,
} from "../api/drive";
import { buildKml, driveImageUrl, timestamp, todayStamp } from "../utils/kmlBuilder";
import { buildKmz } from "../utils/kmzBuilder";
import { useT } from "../i18n";
import { useViewWidth } from "../components/ViewWidthSlider";
import { Row } from "../types/sheets";

const MAP_NAME_KEY = "mew_map_name";
const MAP_FILENAME_KEY = "mew_map_filename";

export default function KmlView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.kml");
  const [rows, setRows] = useState<Row[]>([]);
  const [campusFilter, setCampusFilter] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [savedFileId, setSavedFileId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [commonPictureId, setCommonPictureId] = useState<string | null>(null);
  const [mapName, setMapName] = useState(localStorage.getItem(MAP_NAME_KEY) || "MeW Dispensers");
  const [mapFilename, setMapFilename] = useState(localStorage.getItem(MAP_FILENAME_KEY) || "mew_dispensers");
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const sid = await ensureSpreadsheet();
        setRows(await readAllRows(sid));
        ensureAssetIcon().catch((e) => console.warn("ensureAssetIcon:", e));
        ensureCommonPicture()
          .then((id) => setCommonPictureId(id))
          .catch((e) => console.warn("ensureCommonPicture:", e));
      } catch (e) {
        setStatus(`${t("common.error")}: ${(e as Error).message}`);
      } finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commonPictureUrl = commonPictureId ? driveImageUrl(commonPictureId) : "";

  async function handleCommonPictureUpload(file: File | null | undefined) {
    if (!file) return;
    setBusy(true);
    setStatus(t("kml.uploadingShared"));
    try {
      const id = await uploadCommonPicture(file);
      setCommonPictureId(id);
      setStatus(t("kml.uploadedShared"));
    } catch (e) {
      setStatus(t("kml.uploadFailed", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  function saveSettings() {
    localStorage.setItem(MAP_NAME_KEY, mapName);
    localStorage.setItem(MAP_FILENAME_KEY, mapFilename);
    setStatus(t("kml.settingsSaved"));
  }

  const withGps = useMemo(
    () => rows.filter((r) => r[COL["緯度"]] && r[COL["経度"]]),
    [rows]
  );

  const filtered = useMemo(
    () => withGps.filter((r) => {
      if (campusFilter && !String(r[COL["キャンパス"]] || "").includes(campusFilter)) return false;
      if (buildingFilter && !String(r[COL["建物"]] || "").includes(buildingFilter)) return false;
      return true;
    }),
    [withGps, campusFilter, buildingFilter]
  );

  const selectableIds = useMemo(
    () => filtered.map((r) => String(r[COL["ID"]] || "")).filter(Boolean),
    [filtered]
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  function toggleSelect(id: string) {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((prev) => {
      const isAll = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (isAll) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  async function archiveExistingKmz(mapsFolderId: string, fileName: string) {
    const existing = await findFileInFolder(mapsFolderId, fileName);
    if (!existing) return;
    const archiveFolderId = await resolveOrCreateFolder(mapsFolderId, "ARCHIVE");
    const archivedName = fileName.replace(/\.kmz$/i, "") + `（archive：${timestamp()}）.kmz`;
    await renameAndMoveFile(existing.id, archivedName, archiveFolderId);
  }

  async function generate() {
    if (filtered.length === 0) { setStatus(t("kml.noGps")); return; }
    setBusy(true);
    setStatus(t("kml.generating"));
    try {
      await ensureAssetIcon();
      const iconBlob = await fetchLocalAssetBlob("/assets/icon-1.png");
      const useSelection = selectedIds.size > 0 && !allSelected;
      const toExport = useSelection
        ? filtered.filter((r) => selectedIds.has(String(r[COL["ID"]] || "")))
        : filtered;

      // Photos are referenced by their Drive file ids stored on the row (写真ファイルID);
      // each was made public at upload time, so the public URLs resolve without any API
      // access — which is what lets the owner export photos uploaded by fieldworkers.
      const imagesByRowId = new Map<string, string[]>();
      for (const r of toExport) {
        const id = String(r[COL["ID"]] || "").trim();
        if (!id) continue;
        const fileIds = String(r[COL["写真ファイルID"]] || "").split(",").map((s) => s.trim()).filter(Boolean);
        imagesByRowId.set(id, fileIds.map(driveImageUrl));
      }

      const kml = buildKml(toExport, { mapName, commonPictureUrl, imagesByRowId });
      const kmzBlob = await buildKmz(kml, iconBlob);
      const fileName = `${mapFilename}.kmz`;
      const downloadName = `${todayStamp()}_${fileName}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(kmzBlob);
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(a.href);

      const mapsFolder = await ensureMapsFolder();
      await archiveExistingKmz(mapsFolder, fileName);
      const uploaded = await uploadFileToFolder(mapsFolder, fileName, kmzBlob);
      setSavedFileId(uploaded.id);
      setStatus(t("kml.generated", { count: String(toExport.length) }));
    } catch (e) {
      setStatus(`${t("common.error")}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    <div className="flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-map text-purple-600" aria-hidden="true" />
        {t("kml.title")}
      </h2>

      <details className="border rounded-md p-3 bg-gray-50">
        <summary className="cursor-pointer text-sm font-medium inline-flex items-center gap-2">
          <i className="fa-solid fa-gear text-gray-500" aria-hidden="true" />
          {t("kml.mapSettings")}
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 text-sm">
          <label className="block">
            <span className="text-gray-600">{t("kml.mapName")}</span>
            <input className="w-full border rounded-md px-2 py-1 mt-1" value={mapName} onChange={(e) => setMapName(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-gray-600">{t("kml.filename")}</span>
            <input className="w-full border rounded-md px-2 py-1 mt-1" value={mapFilename} onChange={(e) => setMapFilename(e.target.value)} />
          </label>
          <div className="block md:col-span-2">
            <span className="text-gray-600">{t("kml.commonPicture")}</span>
            <div className="flex items-center gap-3 mt-1">
              {commonPictureUrl ? (
                <img alt="common" src={commonPictureUrl} className="h-20 w-20 object-cover rounded-md border" />
              ) : (
                <div className="h-20 w-20 rounded-md border bg-white flex items-center justify-center text-xs text-gray-400">
                  <i className="fa-regular fa-image text-2xl" aria-hidden="true" />
                </div>
              )}
              <label className="inline-flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 cursor-pointer transition-colors">
                <i className="fa-solid fa-upload" aria-hidden="true" />
                <span>{t("kml.chooseImage")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCommonPictureUpload(e.target.files?.[0])} />
              </label>
            </div>
            {commonPictureId && <div className="text-xs text-gray-500 mt-1 break-all">File ID: {commonPictureId}</div>}
          </div>
        </div>
        <button onClick={saveSettings} className="mt-3 inline-flex items-center gap-2 text-sm bg-gray-700 hover:bg-gray-800 text-white rounded-md px-3 py-1.5 transition-colors">
          <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
          <span>{t("kml.saveSettings")}</span>
        </button>
      </details>

      <div className="flex flex-wrap gap-2">
        <input placeholder={t("kml.filterCampus")} className="border rounded-md px-2 py-1" value={campusFilter} onChange={(e) => setCampusFilter(e.target.value)} />
        <input placeholder={t("kml.filterBuilding")} className="border rounded-md px-2 py-1" value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)} />
      </div>

      <div className="text-sm text-gray-600">
        {t("kml.countSummary", { filtered: String(filtered.length), withGps: String(withGps.length), total: String(rows.length) })}
        {selectedIds.size > 0 && !allSelected && (
          <span className="ml-2 text-purple-700">
            (Selected: {filtered.filter((r) => selectedIds.has(String(r[COL["ID"]] || ""))).length})
          </span>
        )}
      </div>

      <button disabled={busy} onClick={generate}
        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md px-4 py-2 disabled:opacity-50 transition-colors">
        {busy ? (
          <><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /><span>{t("kml.generating")}</span></>
        ) : (
          <><i className="fa-solid fa-file-export" aria-hidden="true" /><span>{t("kml.generate")}</span></>
        )}
      </button>

      {status && (
        <div className="text-sm text-pink-700 flex items-start gap-1.5">
          <i className="fa-solid fa-circle-info mt-0.5" aria-hidden="true" />
          <span>{status}</span>
        </div>
      )}
      {savedFileId && (
        <div className="text-sm">
          <a className="inline-flex items-center gap-1.5 text-blue-700 hover:underline"
            href={driveFileUrl(savedFileId)} target="_blank" rel="noreferrer">
            <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
            <span>{t("kml.openSaved")}</span>
          </a>
        </div>
      )}

      <div className="border-l-4 border-yellow-400 bg-yellow-50 p-3 text-sm flex items-start gap-2">
        <i className="fa-solid fa-lightbulb text-yellow-500 mt-0.5" aria-hidden="true" />
        <span>{t("kml.hint")}</span>
      </div>

      {initialLoading ? (
        <div className="flex flex-col items-center justify-center gap-6 py-24 select-none">
          <div className="relative">
            <img
              src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
              alt="MeW"
              className="w-20 h-20 opacity-90"
            />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      ) : (
        <table className="text-sm w-full mt-4 border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1 text-center w-8">
                <input type="checkbox" checked={selectableIds.length > 0 && allSelected} onChange={() => toggleSelectAll(selectableIds)} disabled={selectableIds.length === 0} />
              </th>
              <th className="border px-2 py-1 text-left">{t("headers.ID")}</th>
              <th className="border px-2 py-1 text-left">{t("headers.表記名①")}</th>
              <th className="border px-2 py-1 text-left">{t("headers.キャンパス")}</th>
              <th className="border px-2 py-1 text-left">{t("headers.建物")}</th>
              <th className="border px-2 py-1 text-left">{t("headers.緯度")}</th>
              <th className="border px-2 py-1 text-left">{t("headers.経度")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const id = String(r[COL["ID"]] || "");
              return (
                <tr key={id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-center">
                    <input type="checkbox" checked={!!id && selectedIds.has(id)} onChange={() => toggleSelect(id)} disabled={!id} />
                  </td>
                  <td className="border px-2 py-1">{id}</td>
                  <td className="border px-2 py-1">{String(r[COL["表記名①"]] || "")}</td>
                  <td className="border px-2 py-1">{String(r[COL["キャンパス"]] || "")}</td>
                  <td className="border px-2 py-1">{String(r[COL["建物"]] || "")}</td>
                  <td className="border px-2 py-1">{String(r[COL["緯度"]] || "")}</td>
                  <td className="border px-2 py-1">{String(r[COL["経度"]] || "")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    </>
  );
}
